import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp, Search } from "lucide-react"

import { cn } from "@/lib/utils"

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    )}
    {...props}>
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}>
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}>
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

// ===== Búsqueda en tiempo real dentro de los dropdowns =====
// Se muestra automáticamente un campo de búsqueda cuando la lista tiene
// SEARCH_MIN_ITEMS o más opciones. Filtrado insensible a mayúsculas y acentos.
const SEARCH_MIN_ITEMS = 6

const normalizeText = (s) =>
  String(s).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")

const getNodeText = (node) => {
  if (node == null || typeof node === "boolean") return ""
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(getNodeText).join(" ")
  if (React.isValidElement(node)) return getNodeText(node.props.children)
  return ""
}

const countSelectItems = (children) => {
  let count = 0
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return
    if (child.type === SelectItem) count += 1
    else if (child.props?.children) count += countSelectItems(child.props.children)
  })
  return count
}

const filterSelectChildren = (children, query) =>
  React.Children.toArray(children)
    .map((child) => {
      if (!React.isValidElement(child)) return child
      if (child.type === SelectItem) {
        const text = normalizeText(getNodeText(child.props.children) || child.props.textValue || child.props.value || "")
        return text.includes(query) ? child : null
      }
      if (child.props?.children && countSelectItems(child.props.children) > 0) {
        const inner = filterSelectChildren(child.props.children, query)
        if (countSelectItems(inner) === 0) return null
        return React.cloneElement(child, undefined, inner)
      }
      return child
    })
    .filter(Boolean)

const SelectContent = React.forwardRef(({ className, children, position = "popper", searchable = true, searchPlaceholder = "Buscar...", ...props }, ref) => {
  const [search, setSearch] = React.useState("")
  const searchInputRef = React.useRef(null)
  const totalItems = countSelectItems(children)
  const showSearch = searchable && totalItems >= SEARCH_MIN_ITEMS
  const query = normalizeText(search.trim())
  const visibleChildren = showSearch && query ? filterSelectChildren(children, query) : children
  const noResults = showSearch && query && countSelectItems(visibleChildren) === 0

  // Foco automático en el buscador al abrir (después de que Radix termine su propio enfoque)
  React.useEffect(() => {
    if (!showSearch) return
    const t = setTimeout(() => searchInputRef.current?.focus({ preventScroll: true }), 100)
    return () => clearTimeout(t)
  }, [showSearch])

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        {...props}
        onKeyDownCapture={(e) => {
          if (!showSearch) return
          if (e.target === searchInputRef.current) return
          // Redirige lo tecleado al buscador (evita el typeahead nativo de Radix)
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault()
            e.stopPropagation()
            setSearch((s) => s + e.key)
            searchInputRef.current?.focus({ preventScroll: true })
          } else if (e.key === "Backspace") {
            e.preventDefault()
            e.stopPropagation()
            setSearch((s) => s.slice(0, -1))
            searchInputRef.current?.focus({ preventScroll: true })
          }
        }}>
        {showSearch && (
          <div
            className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-popover px-3 py-2"
            onKeyDown={(e) => {
              if (!["Escape", "ArrowDown", "ArrowUp", "Tab"].includes(e.key)) e.stopPropagation()
            }}
            onPointerDown={(e) => e.stopPropagation()}>
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={searchInputRef}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="select-search-input"
            />
          </div>
        )}
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn("p-1", position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]")}>
          {visibleChildren}
          {noResults && (
            <div className="px-3 py-2 text-sm text-muted-foreground">Sin resultados</div>
          )}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props} />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}>
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props} />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
